import React, { useState } from 'react';
import {
  Building, Edit, Save, X, Upload, Camera, Star, Award, Globe,
  MapPin, Users, Calendar, Phone, Mail, ExternalLink, Leaf,
  Heart, Shield, Target, TrendingUp, CheckCircle, Plus, Trash2,
  FileText, Image, Link, BarChart3, Eye, Settings
} from 'lucide-react';

const CompanyProfile = ({ company }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [profileData, setProfileData] = useState({
    name: company.name,
    industry: company.industry,
    description: "GreenTech Solutions est une entreprise B-Corp spécialisée dans les technologies durables. Nous développons des solutions innovantes pour réduire l'empreinte carbone des entreprises tout en optimisant leur performance.",
    website: "https://greentech-solutions.com",
    location: company.location,
    size: company.size,
    founded: "2018",
    email: company.email,
    phone: "+33 1 23 45 67 89",
    mission: "Accélérer la transition écologique des entreprises grâce à la technologie",
    values: ["Durabilité", "Innovation", "Transparence", "Collaboration"],
    certifications: company.certifications,
    socialMedia: {
      linkedin: "https://linkedin.com/company/greentech-solutions",
      twitter: "https://twitter.com/greentechsol",
      instagram: "https://instagram.com/greentech.solutions"
    }
  });

  const [newCertification, setNewCertification] = useState('');
  const [newValue, setNewValue] = useState('');

  const stats = [
    { label: "Projets réalisés", value: "47", icon: CheckCircle, color: "text-green-600" },
    { label: "Note moyenne", value: "4.8/5", icon: Star, color: "text-yellow-600" },
    { label: "Freelances collaborés", value: "156", icon: Users, color: "text-blue-600" },
    { label: "Taux de satisfaction", value: "96%", icon: Heart, color: "text-red-600" }
  ];

  const recentProjects = [
    {
      id: 1,
      title: "App Mobile Éco-responsable",
      freelancer: "Camille Dupont",
      status: "Terminé",
      rating: 4.9,
      impact: "Réduction 30% empreinte carbone"
    },
    {
      id: 2,
      title: "Dashboard Analytics RSE",
      freelancer: "Thomas Bernard",
      status: "Terminé",
      rating: 4.8,
      impact: "Amélioration suivi RSE"
    },
    {
      id: 3,
      title: "Stratégie Marketing Digital",
      freelancer: "En cours",
      status: "Actif",
      rating: null,
      impact: "Communication responsable"
    }
  ];

  const testimonials = [
    {
      id: 1,
      freelancer: "Camille Dupont",
      rating: 4.9,
      comment: "GreenTech Solutions est une entreprise exemplaire ! Leurs valeurs et leur approche éthique rendent chaque collaboration enrichissante.",
      project: "App Mobile Éco-responsable",
      date: "Janvier 2024"
    },
    {
      id: 2,
      freelancer: "Thomas Bernard",
      rating: 4.8,
      comment: "Une équipe passionnée et professionnelle. Le projet était clairement défini et aligné avec mes valeurs de développement durable.",
      project: "Dashboard Analytics RSE",
      date: "Décembre 2023"
    }
  ];

  const handleSave = () => {
    // Logic to save profile data
    console.log('Saving profile data:', profileData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    // Reset changes
    setIsEditing(false);
  };

  const addCertification = () => {
    if (newCertification.trim()) {
      setProfileData({
        ...profileData,
        certifications: [...profileData.certifications, newCertification.trim()]
      });
      setNewCertification('');
    }
  };

  const removeCertification = (index) => {
    setProfileData({
      ...profileData,
      certifications: profileData.certifications.filter((_, i) => i !== index)
    });
  };

  const addValue = () => {
    if (newValue.trim()) {
      setProfileData({
        ...profileData,
        values: [...profileData.values, newValue.trim()]
      });
      setNewValue('');
    }
  };

  const removeValue = (index) => {
    setProfileData({
      ...profileData,
      values: profileData.values.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start space-x-6">
          {/* Company Logo */}
          <div className="relative group">
            <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-emerald-400 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-2xl">{company.initials}</span>
            </div>
            {isEditing && (
              <button className="absolute inset-0 bg-black bg-opacity-50 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Camera className="w-6 h-6 text-white" />
              </button>
            )}
          </div>

          <div className="flex-1">
            {isEditing ? (
              <div className="space-y-4">
                <input
                  type="text"
                  value={profileData.name}
                  onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                  className="text-3xl font-bold text-gray-900 bg-transparent border-b-2 border-green-500 focus:outline-none w-full"
                />
                <input
                  type="text"
                  value={profileData.industry}
                  onChange={(e) => setProfileData({...profileData, industry: e.target.value})}
                  className="text-gray-600 bg-transparent border-b border-gray-300 focus:border-green-500 focus:outline-none w-full"
                />
              </div>
            ) : (
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{profileData.name}</h1>
                <p className="text-gray-600 text-lg mb-4">{profileData.industry}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-4">
              {profileData.certifications.map((cert, index) => (
                <div key={index} className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded-lg">
                  <Award className="w-4 h-4" />
                  <span className="text-sm font-medium">{cert}</span>
                  {isEditing && (
                    <button onClick={() => removeCertification(index)} className="ml-1">
                      <X className="w-3 h-3 text-green-600 hover:text-red-600" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <MapPin className="w-4 h-4" />
                <span>{profileData.location}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Users className="w-4 h-4" />
                <span>{profileData.size}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>Depuis {profileData.founded}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 lg:mt-0 flex items-center space-x-4">
          {!isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center space-x-2 px-6 py-3 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                <Edit className="w-4 h-4" />
                <span>Modifier</span>
              </button>
              <button className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200">
                <Eye className="w-4 h-4" />
                <span>Aperçu public</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="flex items-center space-x-2 px-6 py-3 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                <X className="w-4 h-4" />
                <span>Annuler</span>
              </button>
              <button
                onClick={handleSave}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200"
              >
                <Save className="w-4 h-4" />
                <span>Sauvegarder</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-2xl border border-gray-100 p-6 text-center">
            <div className={`w-12 h-12 ${stat.color === 'text-green-600' ? 'bg-green-100' : 
                             stat.color === 'text-yellow-600' ? 'bg-yellow-100' :
                             stat.color === 'text-blue-600' ? 'bg-blue-100' : 'bg-red-100'} 
                             rounded-xl flex items-center justify-center mx-auto mb-3`}>
              <stat.icon className={`w-6 h-6 ${stat.color}`} />
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
            <div className="text-sm text-gray-600">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-200">
          {[
            { id: 'overview', label: 'Vue d\'ensemble', icon: Building },
            { id: 'projects', label: 'Projets', icon: CheckCircle },
            { id: 'testimonials', label: 'Témoignages', icon: Star },
            { id: 'impact', label: 'Impact', icon: Leaf }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 py-4 px-6 font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-green-50 text-green-600 border-b-2 border-green-500'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
                {isEditing ? (
                  <textarea
                    value={profileData.description}
                    onChange={(e) => setProfileData({...profileData, description: e.target.value})}
                    className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows="4"
                  />
                ) : (
                  <p className="text-gray-600 leading-relaxed">{profileData.description}</p>
                )}
              </div>

              {/* Mission & Values */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Mission</h3>
                  {isEditing ? (
                    <textarea
                      value={profileData.mission}
                      onChange={(e) => setProfileData({...profileData, mission: e.target.value})}
                      className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      rows="3"
                    />
                  ) : (
                    <p className="text-gray-600">{profileData.mission}</p>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Valeurs</h3>
                  <div className="space-y-3">
                    {profileData.values.map((value, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Heart className="w-4 h-4 text-green-500" />
                          <span className="text-gray-700">{value}</span>
                        </div>
                        {isEditing && (
                          <button onClick={() => removeValue(index)}>
                            <Trash2 className="w-4 h-4 text-red-500 hover:text-red-700" />
                          </button>
                        )}
                      </div>
                    ))}
                    {isEditing && (
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newValue}
                          onChange={(e) => setNewValue(e.target.value)}
                          placeholder="Nouvelle valeur..."
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                        <button
                          onClick={addValue}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact & Social */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-4 h-4 text-gray-500" />
                      {isEditing ? (
                        <input
                          type="email"
                          value={profileData.email}
                          onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      ) : (
                        <span className="text-gray-700">{profileData.email}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="w-4 h-4 text-gray-500" />
                      {isEditing ? (
                        <input
                          type="tel"
                          value={profileData.phone}
                          onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      ) : (
                        <span className="text-gray-700">{profileData.phone}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <Globe className="w-4 h-4 text-gray-500" />
                      {isEditing ? (
                        <input
                          type="url"
                          value={profileData.website}
                          onChange={(e) => setProfileData({...profileData, website: e.target.value})}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      ) : (
                        <a href={profileData.website} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 flex items-center space-x-1">
                          <span>{profileData.website}</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Certifications</h3>
                  <div className="space-y-2">
                    {profileData.certifications.map((cert, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
                        <div className="flex items-center space-x-3">
                          <Award className="w-4 h-4 text-green-500" />
                          <span className="text-green-700 font-medium">{cert}</span>
                        </div>
                        {isEditing && (
                          <button onClick={() => removeCertification(index)}>
                            <X className="w-4 h-4 text-green-600 hover:text-red-600" />
                          </button>
                        )}
                      </div>
                    ))}
                    {isEditing && (
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newCertification}
                          onChange={(e) => setNewCertification(e.target.value)}
                          placeholder="Nouvelle certification..."
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                        <button
                          onClick={addCertification}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Projets Récents</h3>
                <span className="text-sm text-gray-500">{recentProjects.length} projets</span>
              </div>
              
              {recentProjects.map((project) => (
                <div key={project.id} className="p-6 border border-gray-200 rounded-xl hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{project.title}</h4>
                      <p className="text-sm text-gray-600">Freelance: {project.freelancer}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      project.status === 'Terminé' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {project.rating && (
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-yellow-400" />
                          <span className="text-sm font-medium text-gray-700">{project.rating}</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-2">
                        <Leaf className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-gray-600">{project.impact}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Testimonials Tab */}
          {activeTab === 'testimonials' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Témoignages Freelances</h3>
                <span className="text-sm text-gray-500">{testimonials.length} témoignages</span>
              </div>
              
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="p-6 bg-gray-50 rounded-xl">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center text-white font-bold">
                      {testimonial.freelancer.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{testimonial.freelancer}</h4>
                          <p className="text-sm text-gray-600">{testimonial.project} • {testimonial.date}</p>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-yellow-400" />
                          <span className="font-medium text-gray-700">{testimonial.rating}</span>
                        </div>
                      </div>
                      <p className="text-gray-700 italic">"{testimonial.comment}"</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Impact Tab */}
          {activeTab === 'impact' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Impact Environnemental & Social</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-6 bg-green-50 rounded-xl border border-green-100">
                    <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Leaf className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-3xl font-bold text-green-600 mb-2">-45%</div>
                    <div className="text-sm text-gray-600">Réduction émissions CO2</div>
                  </div>
                  
                  <div className="text-center p-6 bg-blue-50 rounded-xl border border-blue-100">
                    <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-3xl font-bold text-blue-600 mb-2">156</div>
                    <div className="text-sm text-gray-600">Freelances accompagnés</div>
                  </div>
                  
                  <div className="text-center p-6 bg-purple-50 rounded-xl border border-purple-100">
                    <div className="w-16 h-16 bg-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Target className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-3xl font-bold text-purple-600 mb-2">12</div>
                    <div className="text-sm text-gray-600">ODD contribués</div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <h4 className="font-semibold text-green-700 mb-3">Nos Engagements RSE</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-green-700">Neutralité carbone d'ici 2025</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-green-700">50% freelances femmes</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-green-700">Technologies open source privilégiées</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-green-700">Formation continue des équipes</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;